import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  LayoutDashboard,
  FolderKanban,
  ListTodo,
  GanttChart,
  Bell,
  BarChart3,
  GitBranch,
  Users,
  Building2,
  Settings,
  Cog,
  HelpCircle,
  Plus,
  Moon,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useThemeStore } from '../../stores/themeStore';
import { cn } from '../../utils/cn';

interface PaletteItem {
  id: string;
  label: string;
  labelEn?: string;
  icon: LucideIcon;
  action: () => void;
  keywords: string[];
  category: 'navigate' | 'action' | 'setting';
  role?: 'admin' | 'approver';
}

export function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const items = useMemo<PaletteItem[]>(() => {
    const isAdmin = user?.role === 'admin';
    const isApprover = user?.role === 'approver' || isAdmin;

    const all: PaletteItem[] = [
      { id: 'dashboard', label: t('nav.dashboard'), icon: LayoutDashboard, action: () => navigate('/'), keywords: ['ダッシュボード', 'dashboard', 'top', '一覧'], category: 'navigate' },
      { id: 'project-home', label: t('nav.projectHome'), icon: LayoutDashboard, action: () => navigate('/home'), keywords: ['ホーム', 'home'], category: 'navigate' },
      { id: 'projects', label: t('nav.projects'), icon: FolderKanban, action: () => navigate('/projects'), keywords: ['案件', 'projects', 'プロジェクト', 'list'], category: 'navigate' },
      { id: 'my-tasks', label: t('nav.myTasks'), icon: ListTodo, action: () => navigate('/my-tasks'), keywords: ['マイタスク', 'my tasks', 'タスク', 'todo', 'やること'], category: 'navigate' },
      { id: 'gantt', label: t('nav.gantt'), icon: GanttChart, action: () => navigate('/gantt'), keywords: ['ガントチャート', 'gantt', 'timeline', 'スケジュール'], category: 'navigate' },
      { id: 'notifications', label: t('nav.notifications'), icon: Bell, action: () => navigate('/notifications'), keywords: ['通知', 'notifications', 'お知らせ', 'alert'], category: 'navigate' },
      { id: 'settings', label: t('nav.settings'), icon: Settings, action: () => navigate('/settings'), keywords: ['設定', 'settings', '言語', 'language'], category: 'navigate' },
      { id: 'help', label: t('nav.help'), icon: HelpCircle, action: () => navigate('/help'), keywords: ['ヘルプ', 'help', '操作説明', 'guide', 'マニュアル'], category: 'navigate' },
    ];

    if (isApprover) {
      all.push(
        { id: 'reports', label: t('nav.reports'), icon: BarChart3, action: () => navigate('/reports'), keywords: ['レポート', 'reports', '分析', 'analytics'], category: 'navigate', role: 'approver' },
        { id: 'flows', label: t('nav.flows'), icon: GitBranch, action: () => navigate('/admin/flows'), keywords: ['フロー', 'flows', 'テンプレート', 'template'], category: 'navigate', role: 'approver' },
        { id: 'clients', label: t('nav.clients'), icon: Building2, action: () => navigate('/admin/clients'), keywords: ['クライアント', 'clients', '顧客'], category: 'navigate', role: 'approver' },
        { id: 'new-project', label: t('project.create'), icon: Plus, action: () => navigate('/projects/new'), keywords: ['プロジェクト作成', 'create project', '新規', 'new'], category: 'action', role: 'approver' },
      );
    }

    if (isAdmin) {
      all.push(
        { id: 'users', label: t('nav.users'), icon: Users, action: () => navigate('/admin/users'), keywords: ['ユーザー', 'users', '管理'], category: 'navigate', role: 'admin' },
        { id: 'system-settings', label: t('nav.systemSettings'), icon: Cog, action: () => navigate('/admin/settings'), keywords: ['システム設定', 'system settings', '設定'], category: 'navigate', role: 'admin' },
      );
    }

    all.push({
      id: 'toggle-theme',
      label: theme === 'light' ? t('commandPalette.darkMode') : t('commandPalette.lightMode'),
      icon: theme === 'light' ? Moon : Sun,
      action: () => toggleTheme(),
      keywords: ['ダークモード', 'dark mode', 'ライトモード', 'light mode', 'テーマ', 'theme'],
      category: 'setting',
    });

    return all;
  }, [user, navigate, t, theme, toggleTheme]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(item =>
      item.label.toLowerCase().includes(q) ||
      item.keywords.some(k => k.toLowerCase().includes(q))
    );
  }, [items, query]);

  const execute = useCallback((item: PaletteItem) => {
    item.action();
    setOpen(false);
    setQuery('');
    setSelectedIndex(0);
  }, []);

  // Global keyboard shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
        setQuery('');
        setSelectedIndex(0);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && filtered[selectedIndex]) {
        e.preventDefault();
        execute(filtered[selectedIndex]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, filtered, selectedIndex, execute]);

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.children[selectedIndex] as HTMLElement;
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!open) return null;

  const categoryLabels: Record<string, string> = {
    navigate: t('commandPalette.navigate'),
    action: t('commandPalette.actions'),
    setting: t('commandPalette.settings'),
  };

  // Group by category
  const grouped = filtered.reduce<Record<string, PaletteItem[]>>((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  let flatIndex = 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setOpen(false); setQuery(''); }} />
      <div className="relative w-full max-w-lg mx-4 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-in">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <Search className="w-5 h-5 text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('commandPalette.placeholder')}
            className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-80 overflow-y-auto p-2">
          {filtered.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400">
              {t('commandPalette.noResults')}
            </div>
          ) : (
            Object.entries(grouped).map(([category, categoryItems]) => (
              <div key={category}>
                <div className="px-3 py-1.5 text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  {categoryLabels[category]}
                </div>
                {categoryItems.map((item) => {
                  const currentIndex = flatIndex++;
                  return (
                    <button
                      key={item.id}
                      onClick={() => execute(item)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors',
                        currentIndex === selectedIndex
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      )}
                    >
                      <item.icon className="w-4 h-4 shrink-0" />
                      <span className="flex-1 text-left">{item.label}</span>
                      {item.role && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                          {item.role}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px]">↑↓</kbd>
            {t('commandPalette.move')}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px]">Enter</kbd>
            {t('commandPalette.open')}
          </span>
        </div>
      </div>
    </div>
  );
}
